import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, ScrollText, Info } from "lucide-react";
import { Button, Card, Spinner, Tooltip } from "@nutrayan/ui";
import { formatIndianDate } from "@nutrayan/utils";
import {
  listSecurityAudit,
  auditActionLabel,
  describeUserAgent,
  type SecurityAuditView,
} from "@/lib/audit-api";

const PAGE_SIZE = 20;

function OutcomeBadge({ status }: { status?: string | undefined }) {
  const ok = status === "SUCCESS";
  return (
    <span
      className={`text-caption inline-flex items-center rounded-full px-2 py-0.5 font-medium ${
        ok ? "bg-forest/10 text-forest" : "bg-terracotta/10 text-terracotta"
      }`}
    >
      {ok ? "Success" : "Failure"}
    </span>
  );
}

export default function SecurityAuditPage() {
  const [page, setPage] = useState(0);
  const audit = useQuery({
    queryKey: ["admin", "audit", page],
    queryFn: () => listSecurityAudit(page, PAGE_SIZE),
  });

  const rows: SecurityAuditView[] = audit.data ?? [];
  const hasNext = rows.length === PAGE_SIZE;

  return (
    <div className="space-y-6">
      <header>
        <h1 className="font-display text-display-xl text-charcoal">Security Audit</h1>
        <p className="text-body text-ink mt-1">
          Every sign-in, credential change, lock, and admin action — newest first, scoped to your
          tenant.
        </p>
      </header>

      {audit.isPending ? (
        <div className="flex justify-center py-16">
          <Spinner size="lg" />
        </div>
      ) : audit.isError ? (
        <Card>
          <p className="text-body text-terracotta">
            Couldn&apos;t load the audit log. You may not have permission to view it.
          </p>
        </Card>
      ) : rows.length === 0 ? (
        <Card className="flex flex-col items-center py-16 text-center">
          <span className="bg-sand text-muted flex h-14 w-14 items-center justify-center rounded-full">
            <ScrollText size={26} aria-hidden />
          </span>
          <h2 className="font-display text-display-sm text-charcoal mt-4">No events yet</h2>
          <p className="text-body-sm text-ink mt-1">
            Security events will appear here as they occur.
          </p>
        </Card>
      ) : (
        <>
          <Card className="overflow-x-auto p-0">
            <table className="text-body-sm w-full min-w-[52rem]">
              <thead className="border-charcoal/10 text-caption text-muted border-b text-left">
                <tr>
                  <th className="px-4 py-2.5">Time</th>
                  <th className="px-4 py-2.5">Event</th>
                  <th className="px-4 py-2.5">Outcome</th>
                  <th className="px-4 py-2.5">Initiator</th>
                  <th className="px-4 py-2.5">Target</th>
                  <th className="px-4 py-2.5">IP</th>
                  <th className="px-4 py-2.5">Device</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((e) => (
                  <tr key={e.id} className="border-charcoal/5 border-b align-top">
                    <td className="text-ink whitespace-nowrap px-4 py-2.5">
                      {e.createdAt ? formatIndianDate(e.createdAt) : "—"}
                    </td>
                    <td className="text-charcoal px-4 py-2.5 font-medium">
                      {auditActionLabel(e.action)}
                      {e.status === "FAILURE" && e.failureReason ? (
                        <span className="text-caption text-muted mt-0.5 block font-normal">
                          {e.failureReason.replace(/_/g, " ")}
                        </span>
                      ) : null}
                    </td>
                    <td className="px-4 py-2.5">
                      <OutcomeBadge status={e.status} />
                    </td>
                    <td className="text-ink whitespace-nowrap px-4 py-2.5">
                      {e.actorUserId ? `#${e.actorUserId}` : "system"}
                      {e.actorLevel ? (
                        <span className="text-caption text-muted block">{e.actorLevel}</span>
                      ) : null}
                    </td>
                    <td className="text-ink whitespace-nowrap px-4 py-2.5">
                      {e.targetUserId ? `#${e.targetUserId}` : "—"}
                    </td>
                    <td className="text-ink px-4 py-2.5 font-mono">{e.ip ?? "—"}</td>
                    <td className="text-ink px-4 py-2.5">
                      {e.userAgent ? (
                        <Tooltip label={e.userAgent}>
                          <span className="inline-flex items-center gap-1">
                            {describeUserAgent(e.userAgent)}
                            <Info size={12} aria-hidden className="text-muted" />
                          </span>
                        </Tooltip>
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>

          <div className="flex items-center justify-between">
            <p className="text-caption text-muted">Page {page + 1}</p>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                <ChevronLeft size={15} aria-hidden /> Prev
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={!hasNext}
                onClick={() => setPage((p) => p + 1)}
              >
                Next <ChevronRight size={15} aria-hidden />
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
